
import { Button } from "@/components/ui/button";
import { useState, useEffect, useCallback } from "react";
import { getUserSubscriptionStatus } from "@/utils/subscriptionUtils";
import { Loader2, CheckCircle } from "lucide-react";
import PricingDialog from "./PricingDialog";
import { supabase } from "@/integrations/supabase/client";
import { MigrationRunner } from "./MigrationRunner";
import { usePricingPlans } from "@/components/pricing/hooks/usePricingPlans";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "react-router-dom";

export const SubscriptionSettings = () => {
  const { toast } = useToast();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState({
    status: "Free (Forever)",
    planName: "Free (Forever)",
    isActive: true,
    expiresAt: null,
    planId: null
  });
  
  const { plans, isLoading: plansLoading } = usePricingPlans("KES");

  const fetchSubscription = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data: userData } = await supabase.auth.getUser();
      console.log("Current user:", userData?.user?.id);
      
      if (userData?.user) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', userData.user.id)
          .single();
        
        setIsAdmin(profiles?.role === 'admin');
      }
      
      const timestamp = new Date().getTime();
      const subscription = await getUserSubscriptionStatus(`nocache=${timestamp}`);
      console.log("Fetched subscription info:", subscription);
      setSubscriptionInfo(subscription);
    } catch (error) {
      console.error("Error fetching subscription:", error);
      toast({
        title: "Error",
        description: "Failed to fetch subscription details",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const refreshSubscriptionData = async () => {
    try {
      await fetchSubscription();
      toast({
        title: "Refreshed",
        description: "Subscription data has been refreshed",
      });
    } catch (error) {
      console.error("Error refreshing subscription data:", error);
    }
  };
  
  const diagnosePlanIssue = async () => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user?.id) {
        toast({
          title: "Error",
          description: "User not found. Please log in again.",
          variant: "destructive"
        });
        return;
      }
      
      const { data, error } = await supabase.functions.invoke("check-subscription", {
        body: { userId: userData.user.id }
      });
      
      console.log("Subscription diagnostics result:", data);
      
      if (error) {
        console.error("Error checking subscription:", error);
        toast({
          title: "Diagnostics Failed",
          description: "Unable to diagnose subscription issues. Please try again.",
          variant: "destructive"
        });
        return;
      }
      
      if (data.fixApplied) {
        toast({
          title: "Fix Applied",
          description: "Found and fixed an issue with your subscription. Please refresh the page.",
        });
        await fetchSubscription();
        window.location.reload();
      } else if (data.mpesaTransactions && data.mpesaTransactions.length > 0) {
        const completedTx = data.mpesaTransactions.find(tx => tx.status === 'completed');
        
        if (completedTx) {
          toast({
            title: "Completed Payment Found",
            description: "You have a completed payment, but subscription wasn't updated. We're fixing this...",
          });
          
          const { data: manualFixResult, error: manualFixError } = await supabase.functions.invoke("fix-subscription", {
            body: { 
              userId: userData.user.id,
              transactionId: completedTx.id 
            }
          });
          
          console.log("Manual fix result:", manualFixResult);
          
          if (manualFixError) {
            console.error("Error applying manual fix:", manualFixError);
            toast({
              title: "Fix Failed",
              description: "Unable to fix subscription automatically. Please contact support.",
              variant: "destructive"
            });
          } else {
            toast({
              title: "Fix Applied",
              description: "Subscription has been fixed. Please refresh the page.",
            });
            await fetchSubscription();
            window.location.reload();
          }
        } else {
          toast({
            title: "Diagnostics Complete",
            description: "No completed payments found. Please try making a payment again.",
          });
        }
      } else {
        toast({
          title: "Diagnostics Complete",
          description: "No issues found. If you've made a payment, it may still be processing.",
        });
      }
    } catch (error) {
      console.error("Error diagnosing subscription:", error);
      toast({
        title: "Error",
        description: "Failed to diagnose subscription issues",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    const subscriptionsChannel = supabase
      .channel('subscription-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_subscriptions'
        },
        (payload) => {
          console.log('Subscription changed, payload:', payload);
          setTimeout(() => {
            fetchSubscription();
          }, 1000);
        }
      )
      .subscribe();
    
    const mpesaChannel = supabase
      .channel('mpesa-transaction-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'mpesa_transactions',
          filter: 'status=eq.completed'
        },
        (payload) => {
          console.log('M-Pesa transaction completed:', payload);
          setTimeout(() => {
            fetchSubscription();
          }, 3000);
        }
      )
      .subscribe();
    
    const stripeChannel = supabase
      .channel('stripe-payment-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'stripe_payments',
          filter: 'status=eq.completed'
        },
        (payload) => {
          console.log('Stripe payment completed:', payload);
          setTimeout(() => {
            fetchSubscription();
          }, 3000);
        }
      )
      .subscribe();
    
    const chapaChannel = supabase
      .channel('chapa-transaction-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chapa_transactions',
          filter: 'status=eq.completed'
        },
        (payload) => {
          console.log('Chapa transaction completed:', payload);
          setTimeout(() => {
            fetchSubscription();
          }, 3000);
        }
      )
      .subscribe();
    
    console.log("Subscription listeners set up");
    
    fetchSubscription();
    
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('success') === 'true') {
      toast({
        title: "Payment Successful",
        description: "Your subscription has been updated successfully",
      });
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('success');
      window.history.replaceState({}, document.title, cleanUrl.toString());
      
      let checkTimes = [1000, 3000, 5000, 8000, 12000, 20000];
      checkTimes.forEach((delay, index) => {
        setTimeout(() => {
          console.log(`Checking subscription status attempt ${index + 1}`);
          fetchSubscription();
        }, delay);
      });
    } else if (searchParams.get('mpesa') === 'pending') {
      toast({
        title: "M-Pesa Payment Processing",
        description: "Your M-Pesa payment is being processed. Your plan will update shortly.",
      });
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('mpesa');
      window.history.replaceState({}, document.title, cleanUrl.toString());
      
      let checkTimes = [3000, 5000, 8000, 12000, 20000];
      checkTimes.forEach((delay, index) => {
        setTimeout(() => {
          console.log(`Checking subscription status attempt ${index + 1}`);
          fetchSubscription();
        }, delay);
      });
    }
    
    return () => {
      console.log("Cleaning up subscription listeners");
      supabase.removeChannel(subscriptionsChannel);
      supabase.removeChannel(mpesaChannel);
      supabase.removeChannel(stripeChannel);
      supabase.removeChannel(chapaChannel);
    };
  }, [location, toast, fetchSubscription]);

  const handleChangeSubscription = () => {
    setPricingDialogOpen(true);
  };

  const handleDialogOpenChange = (open: boolean) => {
    setPricingDialogOpen(open);
    
    if (!open) {
      console.log("Dialog closed, refreshing subscription data");
      setTimeout(() => {
        fetchSubscription();
      }, 2000);
    }
  };

  if (isLoading || plansLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const currentPlan = subscriptionInfo.planId 
    ? plans.find(plan => plan.id === subscriptionInfo.planId) 
    : plans.find(plan => plan.name === subscriptionInfo.planName) || 
      plans.find(plan => plan.name === "Free (Forever)");
  
  console.log("Current plan found:", currentPlan);
  
  const planFeatures = currentPlan?.features || [];

  return (
    <div>
      {isAdmin && <MigrationRunner />}
      
      <div className="border rounded-lg p-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Plan</p>
              <p className="text-lg font-semibold">{subscriptionInfo.planName}</p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-muted-foreground">Status</p>
              <p className="text-sm font-medium">
                {subscriptionInfo.isActive ? (
                  <span className="text-green-600">Active</span>
                ) : (
                  <span className="text-red-600">Inactive</span>
                )}
              </p>
            </div>
          </div>
          
          {subscriptionInfo.expiresAt && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">Renews On</p>
              <p className="text-sm">
                {new Date(subscriptionInfo.expiresAt).toLocaleDateString()}
              </p>
            </div>
          )}
          
          <div className="mt-4 pt-4 border-t">
            <h4 className="text-sm font-medium text-muted-foreground mb-3">Plan Features</h4>
            {planFeatures.length > 0 ? (
              <ul className="space-y-2">
                {planFeatures.map((feature, index) => {
                  const featureText = typeof feature === 'string' ? feature : feature.text;
                  const featureDescription = typeof feature === 'string' ? undefined : feature.description;
                  
                  return (
                    <li key={index} className="flex items-start text-left">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-sm text-gray-700">{featureText}</span>
                        {featureDescription && (
                          <p className="text-xs text-gray-500 mt-0.5">{featureDescription}</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No features specified for this plan.</p>
            )}
          </div>
          
          <div className="pt-4">
            <Button
              onClick={handleChangeSubscription}
              variant="gradient"
            >
              {subscriptionInfo.planName === "Free (Forever)" ? "Upgrade Plan" : "Change Plan"}
            </Button>
          </div>
        </div>
      </div>
      
      <PricingDialog 
        open={pricingDialogOpen} 
        onOpenChange={handleDialogOpenChange}
        returnUrl="/dashboard/settings/subscription?success=true" 
      />
    </div>
  );
};
